/**
 * Facets and quick filters — "the person question", answered without a
 * definition component (docs/plans/card-editor-dates-groups-filters.md Part 5,
 * Block B).
 *
 * THE OBSERVATION. Every element in a collection whose `lookupCardId` is the
 * People card — or any card descended from it — points at the same people,
 * whatever the element happens to be called. A visit's `resident`, an
 * incident's `reported by` and a shift's `staff` are three names for one
 * question ("which person?"), and asking it today means adding three rows and
 * OR-ing them, which the builder cannot even express.
 *
 * THE ANSWER. One extra root in the grammar: `@<card-key>`. `@people = <id>`
 * is compiled server-side to an `$or` over every card-lookup element in the
 * collection whose target resolves to that card or a descendant, so it is one
 * condition, one row, one chip — and this module is the web half: which facets
 * a collection HAS, what each one is called, and which elements it covers.
 *
 * WHEN A FACET EXISTS. A target card earns one when **two or more differently
 * named elements** point at it: a single lookup element is already its own
 * field, and "Resident (any lookup)" beside "Resident" would be the same
 * question twice. A collection may also name a card explicitly
 * (`collection.facets: [{ cardKey }]`), which is the escape hatch for the
 * collection that wants the chip from day one, before the second element
 * exists.
 *
 * DELIBERATELY PURE — no React, no antd, no HTTP, and NO import of
 * `kqlBuilder` (which imports this): everything here is derivation from card
 * definitions. Reading a facet OUT of a query is `utils/filterValues.js`,
 * which already owns the query half, and the grammar itself
 * (`utils/kql/kql.js`) owns the `@` — this module never spells one into a
 * path.
 */
import { FACET_CMPS } from './kql';
import { normalizeId } from './ids';

/** The sigil a facet is WRITTEN with. It is never part of the stored key. */
export const FACET_PREFIX = '@';

/** The `kind` a facet field carries, so every surface can recognize one. */
export const FACET_KIND = 'facet';

/**
 * The comparisons a facet takes (the frozen wire contract): membership and
 * emptiness, nothing else. A facet has no ordering to be greater than, and
 * `~` over an `$or` of ids is not a question anybody asks. Ordered for the
 * operator select; the grammar's own set is the authority on membership.
 */
export const FACET_OPERATORS = ['eq', 'ne', 'in', 'nin', 'empty', 'nempty']
	.filter(comparison => FACET_CMPS.has(comparison));

/**
 * THE SIGIL IS NOT IN THE PATH. A facet condition's `path` is the bare card
 * key (`['people']`) and its `facet: true` flag is what tells it apart — so
 * nothing downstream can mistake `@` for part of a name, and an ELEMENT
 * literally called `people` stays a different condition entirely. Only two
 * places wear the `@`: the text the user reads, and the field descriptor's
 * `key`, which has to separate the two in one string.
 */
export const facetPath = cardKey => [String(cardKey)];

/** The field-descriptor key for a facet: `@people`, never a bare `people`. */
export const facetFieldKey = cardKey => `${FACET_PREFIX}${cardKey}`;

/** Is this field descriptor a facet? */
export const isFacetField = field => field?.kind === FACET_KIND;

/** The card key a facet field or condition names, or '' for anything else. */
export const facetCardKeyOf = node => (
	node?.facet && Array.isArray(node?.path) && node.path.length === 1 ? node.path[0] : ''
);

/**
 * The value a filter names a card by: its ID, never its key, which moves
 * (deterministic filters, owner ruling d19; keepr-web
 * docs/plans/deterministic-filters.md §3.7 and ruling E11). `@<id>` is a facet
 * key like any other to the grammar and the API (`compile.js facetTargetIds`).
 */
export const cardRefOf = card => normalizeId(card?._id) || '';

/**
 * The OTHER spellings a stored text or setting may name the card by: its key,
 * which everything written before F3 used (`@people`, `facets: [{ cardKey:
 * 'people' }]`, a pinned `@people` chip). Read, never written.
 */
export const cardAliasesOf = card => (card?.key ? [String(card.key)] : []);

/**
 * `card => aliases` among `cards`: a key two readable cards share (a
 * collection's own card and a global one, whether or not a lookup points at
 * the other) is no alias of either — which card text written before F3 meant
 * by it is the server's to say, never this page's guess (the F3 review's F2,
 * F7). One rule for the toolbar's facets and the settings that name them.
 */
export function uniqueCardAliases(cards = []) {
	const keyCount = new Map();
	(cards || []).forEach(card => cardAliasesOf(card).forEach((key) => {
		const folded = key.toLowerCase();
		keyCount.set(folded, (keyCount.get(folded) || 0) + 1);
	}));
	return card => cardAliasesOf(card).filter(key => keyCount.get(key.toLowerCase()) === 1);
}

/** The name a facet wears: the card's plural label ("People"), else its name. */
const cardFacetName = card => (
	card?.label?.plural || card?.label?.singular || card?.name || cardRefOf(card)
);

/** Index of `cards` by normalized id — the hierarchy needs every readable card. */
const indexById = cards => {
	const byId = new Map();
	(cards || []).forEach(card => {
		const id = normalizeId(card?._id);
		if (id) byId.set(id, card);
	});
	return byId;
};

/**
 * A card's id plus every descendant's, because an item of a child card IS an
 * item of its parent — the rule the lookup input and the items endpoint's
 * `include_descendants` already follow. Cycles in the parent chain terminate
 * on the visited set rather than spinning.
 */
export function cardAndDescendantIds(cards, rootId) {
	const root = normalizeId(rootId);
	if (!root) return [];
	const childrenOf = new Map();
	(cards || []).forEach(card => {
		const parent = normalizeId(card?.parentCardId);
		const id = normalizeId(card?._id);
		if (!parent || !id) return;
		if (!childrenOf.has(parent)) childrenOf.set(parent, []);
		childrenOf.get(parent).push(id);
	});
	const out = [];
	const seen = new Set();
	const queue = [root];
	while (queue.length) {
		const id = queue.shift();
		if (seen.has(id)) continue;
		seen.add(id);
		out.push(id);
		(childrenOf.get(id) || []).forEach(child => queue.push(child));
	}
	return out;
}

/** The card-lookup elements of the visible cards, with the card that holds each. */
function lookupElementsOf({ cards, cardIds, getElementsForCard }) {
	const found = [];
	(cards || []).forEach(card => {
		const cardId = normalizeId(card?._id);
		if (!cardId) return;
		if (cardIds && !cardIds.has(cardId)) return;
		const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) : card?.elements) || [];
		elements.forEach(element => {
			if (element?.dataType !== 'card-lookup' || !element?.name) return;
			const target = normalizeId(element?.options?.lookupCardId);
			if (!target) return;
			found.push({ cardId, name: element.name, target });
		});
	});
	return found;
}

/**
 * The facet fields a collection offers — the same descriptor shape every other
 * field wears (`buildFilterFields`), plus what only a facet has:
 *
 *   { key: '@<card id>', path: ['<card id>'], facet: true, kind: 'facet', cardKey: '<card id>',
 *     aliases: ['@people'],              // the key spelling written before F3
 *     label: 'People (any lookup)',      // the builder's field menu
 *     shortLabel: 'People',              // chips and quick-filter chips
 *     dataType: 'card-lookup',           // so the ITEM PICKER renders, and
 *     options: { lookupCardId },         // the chip resolves ids to titles
 *     lookupCardId, targetCardIds: [id, …], memberElements: [{ cardId, name }] }
 *
 * `cardIds` restricts which cards contribute elements (the same restriction
 * the card picker applies), while `cards` stays the full readable set — the
 * hierarchy that decides what "or a descendant" means is not the collection's
 * to narrow. `facetCardKeys` is the collection's explicit list (a Set of card
 * ids — or keys, written before F3); a card named there earns a facet on one
 * element alone.
 */
export function buildFacetFields({ cards = [], cardIds = null, getElementsForCard, facetCardKeys = null, keepKeys = false } = {}) {
	const lookups = lookupElementsOf({ cards, cardIds, getElementsForCard });
	if (!lookups.length) return [];

	const byId = indexById(cards);
	const named = facetCardKeys instanceof Set ? facetCardKeys : new Set(facetCardKeys || []);

	// Every card something points at is a CANDIDATE; which of them earn a
	// facet is decided below, once each one's membership is known.
	const candidates = [...new Set(lookups.map(entry => entry.target))];
	// A key is counted where it is read: this collection's vocabulary (its
	// cards and the globals) and what its lookups point at — not another
	// collection's card that happens to share it, as two installs of one
	// package do (the F3 fix review's N5). Without a vocabulary, every
	// readable card.
	const known = cardIds instanceof Set;
	const candidateIds = new Set(candidates);
	const aliasesOf = uniqueCardAliases(known
		? (cards || []).filter(card => {
			const id = normalizeId(card?._id);
			return cardIds.has(id) || candidateIds.has(id) || card?.scope === 'global';
		})
		: cards);

	return candidates
		.map(targetId => {
			const card = byId.get(targetId);
			const cardKey = cardRefOf(card);
			// A card outside the readable set has no descendants to resolve
			// and nothing to name — the API could not compile `@<nothing>`.
			if (!card || !cardKey) return null;
			const aliases = aliasesOf(card);

			const family = new Set(cardAndDescendantIds(cards, targetId));
			const memberElements = lookups
				.filter(entry => family.has(entry.target))
				.map(({ cardId, name }) => ({ cardId, name }));
			const distinctNames = new Set(memberElements.map(entry => entry.name));

			// One element is already its own field; two are the case this
			// exists for. A collection that says otherwise is obeyed.
			// The setting may name the card by id (written since F3) or by
			// its key (written before).
			if (distinctNames.size < 2 && !named.has(cardKey) && !aliases.some(alias => named.has(alias))) return null;

			const shortLabel = cardFacetName(card);
			return {
				key: facetFieldKey(cardKey),
				path: facetPath(cardKey),
				// `@people` in text, a pin or a setting written before F3 is
				// this same facet (findField, quickFilterFields).
				aliases: aliases.map(facetFieldKey),
				// Read, never rewritten, where the API resolves the text
				// itself (buildFilterFields `keepKeys`).
				...(keepKeys ? { keepKeys: true } : {}),
				forceElement: false,
				// The flag the grammar reads, carried on the descriptor so a
				// row, a chip and a condition all say the same thing.
				facet: true,
				kind: FACET_KIND,
				cardKey,
				label: `${shortLabel} (any lookup)`,
				shortLabel,
				dataType: 'card-lookup',
				options: { lookupCardId: targetId },
				lookupCardId: targetId,
				targetCardIds: [...family],
				memberElements,
				cardLabels: [],
				// A facet is never "only on some card types": it is the union
				// of what every card type says, which is the whole point.
				partial: false,
				system: false,
			};
		})
		.filter(Boolean)
		.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * `element → the facet that covers it`, or null. An element belongs to a facet
 * when the card it points at is that facet's target or a descendant of it;
 * where two facets both cover it (People and the Staff card under it), the
 * BROADER one wins — "filter by Alice" should reach every card that mentions
 * her, not the narrowest slice of them.
 */
export function createFacetResolver(facetFields = []) {
	const facets = (facetFields || []).filter(isFacetField);
	if (!facets.length) return () => null;
	return element => {
		const target = normalizeId(element?.options?.lookupCardId);
		if (!target) return null;
		let best = null;
		facets.forEach(facet => {
			if (!(facet.targetCardIds || []).includes(target)) return;
			const size = (facet.memberElements || []).length;
			if (!best || size > (best.memberElements || []).length) best = facet;
		});
		return best;
	};
}

/**
 * The fields that offer a QUICK FILTER chip beside "+ Filter": every facet,
 * plus every element an editor promoted with `options.quickFilter` — which the
 * card editor offers for the four types whose values are a short, closed set
 * somebody would actually click (choice, yes/no, card lookup, and a user
 * element, whose chip is "Me": `<name> = me`) — and the `tags` system field,
 * whose chip is the tag tree (docs/plans/tags.md §5.2; the page sets its
 * `quickFilter` when there is a tag to pick). Which of them a given VIEW
 * shows is `quickFilterFields` below — a presentation narrowing, decided
 * against the cards the active filter can return.
 */
export const QUICK_FILTER_TYPES = ['choice', 'boolean', 'card-lookup', 'user', 'tag'];

/** What a chip (or a chip's menu) calls a field: a facet's short name, else its label. */
export const quickFilterLabel = field => (isFacetField(field) ? field.shortLabel : field?.label) || '';

/** Is this field eligible for a chip at all? Today's rule, unchanged. */
const isQuickFilterCandidate = field => (
	isFacetField(field)
	// By its TYPE alone: a short text that carries a list of choices is a
	// short text (the list is inert, kept for a change back to Choice —
	// KPE-033), so its `quickFilter` flag offers no chip.
	|| (!!field?.options?.quickFilter && QUICK_FILTER_TYPES.includes(field?.dataType))
);

/**
 * Which cards does a chip belong to?
 *
 * For an element that is the cards that DECLARE it (`cardIds`, which
 * buildFilterFields stamps on every element descriptor). For a facet it is
 * the HOLDER cards of its member lookups — never its target card. `@people`
 * belongs on a Visits list because Visit declares a people lookup; scoping a
 * facet by the card it POINTS AT would delete exactly the chip the feature
 * exists for.
 *
 * A field with no card answer at all (a system field, a facet built from
 * nothing) is treated as always in scope: silence is not evidence of absence.
 */
function chipCardIds(field) {
	if (isFacetField(field)) return (field.memberElements || []).map(entry => entry?.cardId).filter(Boolean);
	return (field?.cardIds || []).filter(Boolean);
}

export const quickFilterInScope = (field, scopeCardIds) => {
	if (!(scopeCardIds instanceof Set)) return true;
	const ids = chipCardIds(field);
	if (!ids.length) return true;
	return ids.some(id => scopeCardIds.has(id));
};

/** Local alias, so the reading below stays a sentence. */
const isInScope = quickFilterInScope;

// A chip key that names elements by id (element ids step 3, ⚑ K5): one
// element, `#k7f3q2xa`, or several cards' elements of one name, `#a|#b`.
const ELEMENT_CHIP_KEY_RE = /^#[a-z0-9]{8}(\|#[a-z0-9]{8})*$/;
const elementIdsOfChipKey = key => (typeof key === 'string' && ELEMENT_CHIP_KEY_RE.test(key)
	? key.split('|').map(part => part.slice(1))
	: []);
const definitionIdsOf = field => (Array.isArray(field?.definitions) ? field.definitions : [])
	.map(def => def?.id).filter(id => typeof id === 'string' && id);

/**
 * THE KEY A CHIP SETTING STORES for a field (`collection.quickFilters`'
 * pinned and hidden lists; element ids step 3, ⚑ K5 — "store #id, as E11
 * moved facets to ids"): an element as its ids — `#k7f3q2xa`, or, where
 * several cards each have their own element of that name, every one,
 * `#a|#b` (sorted) — so a pin follows the element through a rename and never
 * lands on another card's element that takes the name; a facet (and a field
 * with no id) as its key.
 */
export function quickFilterSettingKey(field) {
	const ids = [...new Set(definitionIdsOf(field))].sort();
	if (!field || isFacetField(field) || !ids.length) return field?.key;
	return ids.map(id => `#${id}`).join('|');
}

/**
 * Does a stored chip key name this field? Its key or an alias (a facet's
 * card key, text written before F3; an element's NAME, a setting written
 * before K5) — or element ids, any one of which is the field's.
 */
export function quickFilterKeyNames(field, key) {
	if (!field || typeof key !== 'string') return false;
	if (key === field.key || (Array.isArray(field.aliases) && field.aliases.includes(key))) return true;
	if (isFacetField(field)) return false;
	const ids = elementIdsOfChipKey(key);
	if (!ids.length) return false;
	const own = new Set(definitionIdsOf(field));
	return ids.some(id => own.has(id));
}

/**
 * quickFilterFields(fields, { scopeCardIds, activeKeys, pinned, hidden, limit })
 *
 * THE NARROWING, and the three rules that keep it honest:
 *
 * 1. An ACTIVE chip — one currently holding a top-level condition — is never
 *    dropped, by scope or by hiding. Dropping it would silently remove a
 *    condition from the user's query, or (worse) leave the condition applied
 *    with nothing on screen that can clear it. It renders whatever else is
 *    true about it.
 * 2. A PINNED chip survives any scope. That is what pinning means: the
 *    collection said this question is always worth asking.
 * 3. A HIDDEN chip is dropped even when pinned — naming a key in both lists
 *    is an author contradiction, and the more specific instruction wins. It
 *    is still dropped only from the CHIP ROW: a hidden chip's condition
 *    parses, compiles, filters, and still shows in the applied-chips row.
 *    Hiding is presentation and nothing else.
 *
 * `scopeCardIds` null means unrestricted; an EMPTY Set is a real answer (no
 * card can match) and leaves only the pinned and the active standing.
 *
 * Order: pinned in author order, then the active, then facets by BREADTH (how
 * many elements each stands for, widest first), then the promoted elements,
 * non-partial before partial and alphabetical within. `limit` returns
 * `{ shown, overflow }` so a toolbar can put the tail behind a "More filters"
 * menu; an active chip is never in the overflow. Without it the return is the
 * plain array today's callers expect.
 */
export function quickFilterFields(fields = [], {
	scopeCardIds = null,
	activeKeys = null,
	pinned = [],
	hidden = [],
	limit = null,
} = {}) {
	const pins = [...(pinned || [])];
	const hiddenKeys = [...(hidden instanceof Set ? hidden : new Set(hidden || []))];
	const active = activeKeys instanceof Set ? activeKeys : new Set(activeKeys || []);

	// A facet's chip key is `@<card id>`; a pin, a hide or an applied
	// condition written before F3 spells it `@<card key>` (field.aliases).
	// An element's is its ids (K5) — a setting written before, its name.
	const keysOf = field => [field.key, ...(field.aliases || [])];
	const pinOf = (field) => {
		const at = pins.findIndex(key => quickFilterKeyNames(field, key));
		return at === -1 ? undefined : at;
	};
	const isActive = field => keysOf(field).some(key => active.has(key));
	const isHidden = field => hiddenKeys.some(key => quickFilterKeyNames(field, key));

	const kept = (fields || []).filter(isQuickFilterCandidate).filter(field => {
		if (isActive(field)) return true;
		if (isHidden(field)) return false;
		if (pinOf(field) !== undefined) return true;
		return isInScope(field, scopeCardIds);
	});

	const rank = field => {
		if (pinOf(field) !== undefined) return 0;
		if (isActive(field)) return 1;
		return isFacetField(field) ? 2 : 3;
	};
	const ordered = kept
		.map((field, index) => ({ field, index }))
		.sort((a, b) => {
			const byRank = rank(a.field) - rank(b.field);
			if (byRank) return byRank;
			if (rank(a.field) === 0) return pinOf(a.field) - pinOf(b.field);
			// The active keep the vocabulary's own order: they are already on
			// screen, and re-sorting them under the user would move a chip
			// they are reading.
			if (rank(a.field) === 1) return a.index - b.index;
			if (isFacetField(a.field)) {
				const breadth = (b.field.memberElements || []).length - (a.field.memberElements || []).length;
				if (breadth) return breadth;
			} else if (!a.field.partial !== !b.field.partial) {
				return a.field.partial ? 1 : -1;
			}
			return quickFilterLabel(a.field).localeCompare(quickFilterLabel(b.field));
		})
		.map(entry => entry.field);

	if (!Number.isInteger(limit) || limit < 0) return ordered;

	const shown = [];
	const overflow = [];
	ordered.forEach(field => {
		if (shown.length < limit || isActive(field)) shown.push(field);
		else overflow.push(field);
	});
	return { shown, overflow };
}

